/**
 * localStorage wrapped in try/catch: Safari private mode, blocked cookies and
 * quota errors must never crash the game. Every key is namespaced `wordloom:`.
 */
export const KEYS = {
  name: 'wordloom:name',
  theme: 'wordloom:theme',
  sound: 'wordloom:sound',
  hintsDismissed: 'wordloom:hints-dismissed',
  recent: 'wordloom:recent',
  seat: (code: string) => `wordloom:seat:${code.toUpperCase()}`,
  rack: (code: string, gameNumber: number) => `wordloom:rack:${code.toUpperCase()}:${gameNumber}`,
} as const;

export function readString(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeString(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — the value simply won't persist */
  }
}

export function removeKey(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function readJSON<T>(key: string, isValid: (v: unknown) => v is T): T | null {
  const raw = readString(key);
  if (raw == null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeJSON(key: string, value: unknown): void {
  writeString(key, JSON.stringify(value));
}

export function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

// --- Seat tokens & recent rooms -------------------------------------------

export function getSeatToken(code: string): string | null {
  return readString(KEYS.seat(code));
}

export function setSeatToken(code: string, token: string): void {
  writeString(KEYS.seat(code), token);
}

export function clearSeatToken(code: string): void {
  removeKey(KEYS.seat(code));
}

export interface RecentRoom {
  code: string;
  /** Opponent's name when known, for the Resume list. */
  opponent: string | null;
  /** Epoch ms of the last visit. */
  at: number;
}

function isRecentRooms(v: unknown): v is RecentRoom[] {
  return (
    Array.isArray(v) &&
    v.every(
      (r) =>
        typeof r === 'object' &&
        r !== null &&
        typeof (r as RecentRoom).code === 'string' &&
        typeof (r as RecentRoom).at === 'number',
    )
  );
}

export const RECENT_LIMIT = 5;

/** Recent rooms (newest first) that still have a stored seat token. */
export function getRecentRooms(): RecentRoom[] {
  const list = readJSON(KEYS.recent, isRecentRooms) ?? [];
  return list.filter((r) => getSeatToken(r.code) != null).slice(0, RECENT_LIMIT);
}

export function rememberRoom(code: string, opponent: string | null): void {
  const upper = code.toUpperCase();
  const list = (readJSON(KEYS.recent, isRecentRooms) ?? []).filter((r) => r.code !== upper);
  const previous = (readJSON(KEYS.recent, isRecentRooms) ?? []).find((r) => r.code === upper);
  list.unshift({ code: upper, opponent: opponent ?? previous?.opponent ?? null, at: Date.now() });
  writeJSON(KEYS.recent, list.slice(0, RECENT_LIMIT));
}

export function forgetRoom(code: string): void {
  const upper = code.toUpperCase();
  const list = (readJSON(KEYS.recent, isRecentRooms) ?? []).filter((r) => r.code !== upper);
  writeJSON(KEYS.recent, list);
  clearSeatToken(upper);
}
